// ============================================================================
// 📁 FILE: src/modules/ticket/services/slaService.js
// 📝 PURPOSE:
//    Centralized SLA API wrapper for Ticketing Module.
//    Provides:
//      - getTicketSLA(ticketId, scope)
//      - getEscalationLog(ticketId, scope)
//      - getSlaProfiles(tenantId, scope)
//
// 🔧 DESIGN:
//    - Uses axiosClient from src/api/axiosClient (no "@/..." alias).
//    - Uses native URLSearchParams (no "query-string" dependency).
//    - Multi-tenant safe via scope params: { tenant_id, location_id }.
// ============================================================================

import axiosClient from "../../../api/axiosClient";

// ---------------------------------------------------------------------------
// Base URL
// ---------------------------------------------------------------------------
const BASE_URL = "/ticket-management";

// ---------------------------------------------------------------------------
// Normalization Helper
// ---------------------------------------------------------------------------
function normalizeSLAData(apiData) {
  if (!apiData) return null;

  return {
    // match UI expectations (snake_case)
    sla_status: apiData.sla_status || apiData.slaStatus,
    pause_reason: apiData.pause_reason || apiData.pauseReason || null,
    escalation_level: apiData.escalation_level ?? apiData.escalationLevel,
    breach_type: apiData.breach_type ?? apiData.breachType,

    response: apiData.response
      ? {
          due_at: apiData.response.due_at || apiData.response.dueAt,
          remaining_seconds:
            apiData.response.remaining_seconds ??
            apiData.response.remainingSeconds,
        }
      : null,

    resolution: apiData.resolution
      ? {
          due_at: apiData.resolution.due_at || apiData.resolution.dueAt,
          remaining_seconds:
            apiData.resolution.remaining_seconds ??
            apiData.resolution.remainingSeconds,
        }
      : null,
  };
}


// ---------------------------------------------------------------------------
// API: Get SLA for Ticket
// GET /ticket-management/tickets/<id>/sla?tenant_id=..&location_id=..
// ---------------------------------------------------------------------------
export async function getTicketSLA(ticketId, scope = {}) {
  try {
    const query = new URLSearchParams(scope).toString();
    const url = query
      ? `${BASE_URL}/tickets/${ticketId}/sla?${query}`
      : `${BASE_URL}/tickets/${ticketId}/sla`;

    const { data } = await axiosClient.get(url);

    if (!data?.success) {
      throw new Error(data?.message || "Failed to fetch SLA");
    }

    return normalizeSLAData(data.data);
  } catch (err) {
    console.error("getTicketSLA failed:", err);
    throw err;
  }
}

// ---------------------------------------------------------------------------
// API: Get Escalation Log
// GET /ticket-management/tickets/<id>/escalation-log?tenant_id=..&location_id=..
// ---------------------------------------------------------------------------
export async function getEscalationLog(ticketId, scope = {}) {
  try {
    const query = new URLSearchParams(scope).toString();
    const url = query
      ? `${BASE_URL}/tickets/${ticketId}/escalation-log?${query}`
      : `${BASE_URL}/tickets/${ticketId}/escalation-log`;

    const { data } = await axiosClient.get(url);

    if (!data?.success) {
      throw new Error(data?.message || "Failed to fetch escalation log");
    }

    return data.data || [];
  } catch (err) {
    console.error("getEscalationLog failed:", err);
    throw err;
  }
}

// ---------------------------------------------------------------------------
// API: Get SLA Profiles for Admin UI
// GET /ticket-management/sla-profiles?tenant_id=<id>&...
// ---------------------------------------------------------------------------
export async function getSlaProfiles(tenantId, scope = {}) {
  try {
    const paramsObj = {
      tenant_id: tenantId,
      ...scope,
    };
    const query = new URLSearchParams(paramsObj).toString();
    const url = `${BASE_URL}/sla-profiles?${query}`;

    const { data } = await axiosClient.get(url);

    if (!data?.success) {
      throw new Error(data?.message || "Failed to fetch SLA profiles");
    }

    return data.data || [];
  } catch (err) {
    console.error("getSlaProfiles failed:", err);
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Default Export (Repository Pattern)
// ---------------------------------------------------------------------------
const slaService = {
  getTicketSLA,
  getEscalationLog,
  getSlaProfiles,
};

export default slaService;
